import { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../axiosInstance';

const BASE = '/flights/flight-statuses';

/**
 * Map API flight status to UI format
 * API: { id, code, name, category, description, color, icon, usedByCount, ... }
 * UI:  { id, status, code, description, icon, color, usedBy, usage }
 */
export const mapApiToUi = (api) => {
  if (!api) return null;
  const defaultUsedBy = [
    'API Flights',
    'Manual Flights',
    'Booking System',
    'Schedule Updater',
    'Customer Communication',
  ];
  return {
    id: api.id,
    status: api.name ?? '',
    code: api.code ?? '',
    category: api.category ?? '',
    description: api.description ?? '',
    icon: api.icon ?? '📋',
    color: api.color ?? '#6b7280',
    usedBy: defaultUsedBy,
    usage: api.description ?? 'System-defined status used across flight operations',
  };
};

/**
 * useGetFlightStatuses - fetch flight statuses with params
 * @param {Object} params - { page, limit, search, category }
 * @returns { data, pagination, loading, error, refetch }
 */
export const useGetFlightStatuses = (params = {}) => {
  const {
    page = 1,
    limit = 100,
    search = '',
    category = '',
  } = params;

  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStatuses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const queryParams = { page, limit };
      if (search) queryParams.search = search;
      if (category) queryParams.category = category;

      const response = await axiosInstance.get(BASE, { params: queryParams });
      const res = response.data;

      if (res.success) {
        const mapped = (res.data || []).map(mapApiToUi);
        setData(mapped);
        if (res.pagination) {
          setPagination(res.pagination);
        }
      } else {
        setError(res.message || 'Failed to load flight statuses');
        setData([]);
      }
    } catch (err) {
      if (err.response) {
        setError(err.response?.data?.message || err.response?.data?.error || `Request failed (${err.response.status})`);
      } else if (err.request) {
        setError('Cannot reach server. Check that the backend is running (e.g. http://localhost:5000).');
      } else {
        setError(err.message || 'Failed to load flight statuses');
      }
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, category]);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  const refetch = useCallback(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  return { data, pagination, loading, error, refetch };
};
